import type { LeaderboardEntry } from "../../shared/types";
import { formatPct, returnColor } from "../lib/format";
import { ManagerAvatar } from "./ManagerAvatar";
import { SectionHeader } from "./ui";

/** Green->red intensity by magnitude, capped at ±40% so the scale stays useful. */
function heatColor(r: number): string {
  const mag = Math.min(Math.abs(r) / 0.4, 1);
  const a = (0.1 + mag * 0.62).toFixed(2);
  return r >= 0 ? `rgba(22,199,132,${a})` : `rgba(234,57,67,${a})`;
}

/** The whole league at a glance: managers x slots, colored by return. */
export function LeagueHeatmap({ entries }: { entries: LeaderboardEntry[] }) {
  if (entries.length === 0) return null;
  const cols = entries.reduce((m, e) => Math.max(m, e.slotReturns.length), 0);
  return (
    <div className="panel p-4">
      <SectionHeader
        title="League heatmap"
        right={<span className="text-[11px] text-faint">return by slot</span>}
      />
      <div className="overflow-x-auto">
        <table className="w-full border-separate" style={{ borderSpacing: 3 }}>
          <thead>
            <tr>
              <th />
              {Array.from({ length: cols }, (_, j) => (
                <th key={j} className="num pb-1 text-[10px] font-semibold text-faint">
                  {j + 1}
                </th>
              ))}
              <th className="num pb-1 pl-1 text-[10px] font-semibold uppercase tracking-wider text-faint">
                avg
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td className="pr-3">
                  <div className="flex items-center gap-2">
                    <ManagerAvatar name={e.name} seed={e.seed} size={24} ring={false} />
                    <span className="whitespace-nowrap text-sm font-semibold text-fg">
                      {e.name}
                    </span>
                  </div>
                </td>
                {Array.from({ length: cols }, (_, j) => {
                  const r = e.slotReturns[j];
                  if (r == null) {
                    return (
                      <td key={j}>
                        <div className="h-9 w-9 rounded bg-ink-800/60" />
                      </td>
                    );
                  }
                  return (
                    <td key={j}>
                      <div
                        className="num flex h-9 w-9 items-center justify-center rounded text-[10px] font-semibold text-fg/90"
                        style={{ backgroundColor: heatColor(r) }}
                        title={`${e.name} · slot ${j + 1}: ${formatPct(r)}`}
                      >
                        {Math.round(r * 100)}
                      </div>
                    </td>
                  );
                })}
                <td className="pl-1">
                  <div className={`num text-center text-sm font-bold ${returnColor(e.avgReturn)}`}>
                    {formatPct(e.avgReturn, 1)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
